import { zodResolver } from '@hookform/resolvers/zod';
import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import { useIsCustomerMode } from '@plato/app-core/hooks/use-customer-mode';
import { DialogForm } from '@plato/ui-legacy/components/dialog/dialog-form';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import type { DialogInnerProps } from '@plato/ui-legacy/components/dialog/types';
import { Button } from '@plato/ui-legacy/components/ui/button';
import { Input } from '@plato/ui-legacy/components/ui/input';
import { InputCurrency, InputNumber } from '@plato/ui-legacy/components/ui/input-number';
import { useFormExtended } from '@plato/ui-legacy/lib/hooks/use-form-extended';
import { useTrackedFieldChanges } from '@plato/ui-legacy/lib/hooks/use-tracked-field-changes';
import { Dialog } from '@plato/ui/containers/dialog';
import { useMutation } from '@tanstack/react-query';
import { isEqual } from 'lodash-es';
import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, type ReactElement } from 'react';
import { Controller, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDebounceCallback } from 'usehooks-ts';
import z from 'zod';
import {
    salesDiscountSchema,
    type SalesDraftNormalLineItem,
    type SalesDraftOrder,
} from '../../../server/types/drafts-types.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { draftLineItemFields } from '../../analytics/types/analytics-events.ts';
import { VoiceRecordingButton } from '../../voice-recording/components/voice-recording-button.tsx';
import { IncrementalQuantityFieldWithDialog } from '../form/incremental-quantity-field.tsx';
import { PercentInput } from '../form/percent-input.tsx';
import { ShippingTypeInput } from '../form/shipping-type-input.tsx';
import { StorageLocationInput } from '../form/storage-location-input.tsx';
import { UnitPriceInput } from '../form/unit-price-input.tsx';
import { useDraftContext } from '../hooks/use-draft-context.ts';
import { useToasts } from '../hooks/use-toasts.ts';

export type LineItemEditDialogProps = {
    draft: SalesDraftOrder;
    lineItem: SalesDraftNormalLineItem;
};

export const LineItemEditDialog = DialogStateManager.create(LineItemEditDialogInner);

const lineItemEditFormSchema = z.object({
    note: z.string(),
    price: z.number(),
    quantity: z.number(),
    rebate: z.array(salesDiscountSchema),
    shipping_type_id: z.string(),
    storage_id: z.string(),
});

type LineItemEditForm = z.infer<typeof lineItemEditFormSchema>;

const calculatePriceRelatedFields = ['quantity', 'price', 'rebate', 'storage_id'] as const;

function LineItemEditDialogInner({ lineItem }: LineItemEditDialogProps & DialogInnerProps): ReactElement {
    const { remove, visible } = useDialog();
    const { t } = useTranslation('sales-draft');
    const apiUtils = api.useUtils();
    const { showUpdateErrorToast } = useToasts();
    const { draft, setDraft } = useDraftContext({ refetchOnMount: false });
    const sendEvent = useAnalytics();

    const customerViewToggled = useIsCustomerMode();
    const {
        enableVoiceRecording,
        canManageTransactionDraftNotes,
        canReadGrossMargin,
        canReadTransactionGrossMargin,
        canReadLineItemRebate,
        canReadLineItemPurchasePrice,
        canReadDraftLineItemBackorder,
        canUpdateLineItemPrice,
        canUpdateLineItemRebate,
        canUpdateTransactionShippingType,
        canUpdateLineItemStorageLocation,
        defaultCurrency,
    } = useAppConfig();

    const {
        control,
        formState: { errors, isValid },
        getValues,
        handleSubmit: handleSubmit,
        setValue,
        subscribe,
        watch,
    } = useFormExtended({
        mode: 'all',
        defaultValues: {
            note: lineItem.note ?? '',
            price: lineItem.price,
            quantity: lineItem.quantity,
            rebate: lineItem.rebate ?? [],
            shipping_type_id: lineItem.shipping_type_id,
            storage_id: lineItem.storage_id,
        } satisfies LineItemEditForm,
        resolver: zodResolver(lineItemEditFormSchema),
    });

    const { mutate: submit, isPending: isSubmitting } =
        api.platoUser.command.salesDraft.lineItems.updateNormalLineItem.useMutation({
            onError: showUpdateErrorToast,
            onSuccess: (data) => {
                setDraft(data);
                remove();
            },
        });

    const { data: salesConfig, isLoading: isLoadingSalesConfig } =
        api.platoUser.query.salesDraft.getSalesDraft.getSalesConfig.useQuery({
            product_variant_id: lineItem.product_variant_id,
        });
    const salesUnit = salesConfig?.quantity_unit_sales ?? salesConfig?.package_unit;

    const onSubmit = handleSubmit((data) => {
        if (!salesUnit || !draft) {
            return;
        }
        submit({
            draft_line_item_id: lineItem.line_item_id,
            draft_order_id: draft.draft_order_id,
            external: lineItem.external,
            note: data.note,
            price: data.price,
            product_variant_id: lineItem.product_variant_id,
            quantity: data.quantity,
            quantity_unit: salesUnit,
            rebate: data.rebate,
            shipping_type_id: data.shipping_type_id,
            storage_id: data.storage_id,
        });
    });

    const { fields: rebateFields } = useFieldArray({
        control,
        name: 'rebate',
    });

    const { data: configResult, error: stockError } =
        api.platoUser.query.salesDraft.getInventory.getProductVariantConfig.useQuery(
            { productVariantId: lineItem.product_variant_id },
            { enabled: canReadDraftLineItemBackorder },
        );

    const liveStorageId = watch('storage_id') || draft?.storage_id;
    const currentStock = configResult?.stock.find((x) => x.storage_id === liveStorageId)?.effective;

    const {
        data: priceResult,
        mutate: calculatePrice,
        isPending: isCalculatingPrice,
    } = useMutation({
        mutationFn: async (values: Omit<LineItemEditForm, 'price'> & { price?: number }) => {
            if (!draft) {
                return;
            }
            const { price } = await apiUtils.platoUser.query.salesDraft.getSalesDraft.getPrice.fetch({
                customer_id: draft.customer_id,
                product_variant_id: lineItem.product_variant_id,
                quantity: values.quantity,
                quantity_unit: lineItem.quantity_unit,
                rebate: values.rebate,
                storage_id: values.storage_id,
                price: values.price,
            });

            if (price.price !== values.price) {
                setValue('price', price.price);
            }

            if (!isEqual(price.rebate, values.rebate)) {
                setValue('rebate', price.rebate);
            }

            return price;
        },
    });

    const total = priceResult?.total ?? lineItem.total;
    const margin = priceResult?.margin ?? lineItem.gross_margin;

    const recalculatePrice = useDebounceCallback(
        useCallback(() => {
            calculatePrice(getValues());
        }, [calculatePrice, getValues]),
        1000,
    );

    useEffect(() => {
        const unsubscribe = subscribe({
            name: calculatePriceRelatedFields,
            formState: { values: true },
            callback: ({ type }) => {
                if (type === 'change') {
                    recalculatePrice();
                }
            },
        });

        return unsubscribe;
    }, [recalculatePrice, subscribe]);

    useTrackedFieldChanges({
        subscribe,
        fields: draftLineItemFields,
        onFieldChange: (field) => {
            if (!draft) {
                return;
            }
            sendEvent('draft_line_item_field_changed', {
                draft_id: draft.draft_order_id,
                line_item_id: lineItem.line_item_id,
                product_variant_id: lineItem.product_variant_id,
                field,
            });
        },
    });

    const quantity = watch('quantity');

    function handleResetPrice(): void {
        calculatePrice({ ...getValues(), price: undefined, rebate: [] });
    }

    return (
        <Dialog open={visible} onClose={remove} disablePointerDismissal>
            <DialogForm className="sm:max-w-2xl" onSubmit={onSubmit}>
                <DialogForm.Header title={lineItem.product_variant_name} />
                <DialogForm.Content>
                    <DialogForm.ItemList>
                        <DialogForm.Grid>
                            <DialogForm.Item col>
                                <DialogForm.Label>{t('sales_draft__item_form__quantity')}</DialogForm.Label>
                                <DialogForm.Body>
                                    <Controller
                                        control={control}
                                        name="quantity"
                                        render={({ field }) => (
                                            <IncrementalQuantityFieldWithDialog
                                                currency={defaultCurrency}
                                                name={field.name}
                                                quantityUnit={salesUnit ?? ''}
                                                salesUnitsPerPackageUnit={
                                                    salesConfig?.sales_units_per_package_unit
                                                }
                                                partialPackingType={salesConfig?.partial_packing_type}
                                                onBlur={field.onBlur}
                                                onValueChange={field.onChange}
                                                value={field.value}
                                                isLoading={isLoadingSalesConfig || isCalculatingPrice}
                                            />
                                        )}
                                    />
                                </DialogForm.Body>
                                <DialogForm.Error error={errors.quantity} />
                                {stockError ? (
                                    <div className="text-sm text-red-600">
                                        {t('sales_draft__error_stock')}
                                    </div>
                                ) : canReadDraftLineItemBackorder &&
                                  currentStock !== undefined &&
                                  currentStock < quantity ? (
                                    <div className="text-sm text-red-600">
                                        {t('backorder', { ns: 'common' })}: {quantity - currentStock}
                                    </div>
                                ) : undefined}
                            </DialogForm.Item>
                            <DialogForm.Item col>
                                <DialogForm.Label>{t('sales_draft__item_form__price')}</DialogForm.Label>
                                <DialogForm.Body>
                                    <Controller
                                        control={control}
                                        name="price"
                                        render={({ field }) => (
                                            <UnitPriceInput
                                                name={field.name}
                                                disabled={!canUpdateLineItemPrice}
                                                onBlur={field.onBlur}
                                                onValueChange={field.onChange}
                                                scaleFactor={salesConfig?.price_scale_factor}
                                                unit={salesUnit}
                                                value={field.value}
                                                loading={isCalculatingPrice}
                                            />
                                        )}
                                    />
                                </DialogForm.Body>
                                <DialogForm.Error error={errors.price} />
                            </DialogForm.Item>
                        </DialogForm.Grid>
                        <DialogForm.Grid>
                            {canReadLineItemRebate && (
                                <DialogForm.Item col>
                                    <DialogForm.Label>{t('sales_draft__item_form__rebate')}</DialogForm.Label>
                                    <DialogForm.Body>
                                        <div className="flex flex-auto gap-2">
                                            {rebateFields.map((field, index) => (
                                                <Controller
                                                    key={field.id}
                                                    control={control}
                                                    name={`rebate.${index}.percent`}
                                                    render={({ field }) => (
                                                        <>
                                                            <PercentInput
                                                                name={field.name}
                                                                disabled={!canUpdateLineItemRebate}
                                                                loading={isCalculatingPrice}
                                                                onBlur={field.onBlur}
                                                                onValueChange={field.onChange}
                                                                value={field.value}
                                                            />
                                                        </>
                                                    )}
                                                />
                                            ))}
                                            {canUpdateLineItemRebate && (
                                                <Button
                                                    variant="subtle"
                                                    onClick={handleResetPrice}
                                                    disabled={isCalculatingPrice || !isValid}
                                                >
                                                    <RefreshCw className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                    </DialogForm.Body>
                                </DialogForm.Item>
                            )}
                            {canReadGrossMargin && canReadTransactionGrossMargin && !customerViewToggled && (
                                <DialogForm.Item col>
                                    <DialogForm.Label>
                                        {t('sales_draft__item_form__gross_margin_percent')}
                                    </DialogForm.Label>
                                    <DialogForm.Body>
                                        <InputNumber
                                            currency={defaultCurrency}
                                            disabled
                                            value={total ? (100 * margin) / total : 0}
                                            suffix={`%`}
                                            decimalScale={2}
                                        />
                                    </DialogForm.Body>
                                </DialogForm.Item>
                            )}
                        </DialogForm.Grid>
                        <DialogForm.Grid>
                            {canReadGrossMargin && canReadTransactionGrossMargin && !customerViewToggled && (
                                <DialogForm.Item col>
                                    <DialogForm.Label>
                                        {t('sales_draft__item_form__gross_margin')}
                                    </DialogForm.Label>
                                    <DialogForm.Body>
                                        <InputCurrency
                                            currency={defaultCurrency}
                                            disabled
                                            loading={isCalculatingPrice}
                                            value={margin}
                                        />
                                    </DialogForm.Body>
                                </DialogForm.Item>
                            )}
                            <DialogForm.Item col>
                                <DialogForm.Label>{t('sales_draft__item_form__total')}</DialogForm.Label>
                                <DialogForm.Body>
                                    <InputCurrency
                                        currency={defaultCurrency}
                                        disabled
                                        loading={isCalculatingPrice}
                                        decimalScale={2}
                                        value={total}
                                    />
                                </DialogForm.Body>
                            </DialogForm.Item>
                        </DialogForm.Grid>
                        {canReadLineItemPurchasePrice && (
                            <DialogForm.Item col>
                                <DialogForm.Label>
                                    {t('sales_draft__item_form__encoded_price')}
                                </DialogForm.Label>
                                <DialogForm.Body>
                                    <Input
                                        disabled
                                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- legacy code
                                        value={lineItem.external.encoded_price}
                                        className="[&>input]:text-right"
                                    />
                                </DialogForm.Body>
                            </DialogForm.Item>
                        )}
                        {canUpdateLineItemStorageLocation && (
                            <DialogForm.Item col>
                                <DialogForm.Label>{t('sales_draft__item_form__storage')}</DialogForm.Label>
                                <DialogForm.Body>
                                    <Controller
                                        control={control}
                                        name="storage_id"
                                        render={({ field }) => (
                                            <StorageLocationInput
                                                className="w-full"
                                                headerStorageId={draft?.storage_id}
                                                onChange={field.onChange}
                                                placeholder={t('placeholder__select_storage', {
                                                    ns: 'common',
                                                })}
                                                value={field.value}
                                            />
                                        )}
                                    />
                                </DialogForm.Body>
                                <DialogForm.Error error={errors.storage_id} />
                            </DialogForm.Item>
                        )}
                        {canUpdateTransactionShippingType && (
                            <DialogForm.Item col>
                                <DialogForm.Label>
                                    {t('sales_draft__item_form__shipping_type_id')}
                                </DialogForm.Label>
                                <DialogForm.Body>
                                    <Controller
                                        control={control}
                                        name="shipping_type_id"
                                        render={({ field, formState }) => (
                                            <ShippingTypeInput
                                                className="w-full"
                                                onChange={field.onChange}
                                                disabled={formState.disabled}
                                                value={field.value}
                                            />
                                        )}
                                    />
                                </DialogForm.Body>
                                <DialogForm.Error error={errors.shipping_type_id} />
                            </DialogForm.Item>
                        )}
                        {canManageTransactionDraftNotes && (
                            <DialogForm.Item col>
                                <DialogForm.Label>{t('sales_draft__item_form__note')}</DialogForm.Label>
                                <DialogForm.Body>
                                    <Controller
                                        control={control}
                                        name="note"
                                        render={({ field }) => (
                                            <div className="flex flex-auto gap-2">
                                                <Input
                                                    value={field.value}
                                                    onBlur={field.onBlur}
                                                    onChange={field.onChange}
                                                />
                                                {enableVoiceRecording && (
                                                    <VoiceRecordingButton
                                                        trackingContext={{ source: 'draft_line_item' }}
                                                        onTranscription={(text) => {
                                                            field.onChange(
                                                                field.value ? `${field.value} ${text}` : text,
                                                            );
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        )}
                                    />
                                </DialogForm.Body>
                            </DialogForm.Item>
                        )}
                    </DialogForm.ItemList>
                </DialogForm.Content>
                <DialogForm.SubmitBlock disabled={isSubmitting} isLoading={isSubmitting} onCancel={remove} />
            </DialogForm>
        </Dialog>
    );
}
